import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import { SkButtonComponent } from '@mercadoni/stark-ui';

import type { AppError } from '../../../core/interfaces';
import { translateNow, translationVersion } from '../../../core/services/i18n/translate-now';
import { NfErrorAlertComponent } from '../nf-error-alert/nf-error-alert.component';

/**
 * Load-failure state for a list, page or sidebar section: it takes the place of the empty state
 * when a listener or request fails, so a failed load is never mistaken for "no data".
 *
 * Shows a title, the translated message with its technical detail and error id, and a
 * `Reintentar` button. While the retry runs (`loading`) the button reads `Cargando…` and is
 * disabled. Use `compact` inside narrow areas such as the sidebar.
 */
@Component({
  selector: 'nf-error-state',
  imports: [NfErrorAlertComponent, SkButtonComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (compact()) {
      <div data-compact class="flex flex-col gap-2 p-2">
        <p class="text-xs font-semibold text-sk-text">{{ shownTitle() }}</p>
        <nf-error-alert [error]="error()" />
        @if (retryable()) {
          <sk-button variant="outline" size="sm" [disabled]="loading()" (skClick)="retry.emit()">
            {{ retryLabel() }}
          </sk-button>
        }
      </div>
    } @else {
      <div class="mx-auto flex max-w-xl flex-col items-center gap-4 rounded-xl border border-sk-border bg-sk-surface p-8 text-center">
        <h2 class="text-base font-semibold text-sk-text">{{ shownTitle() }}</h2>
        <div class="w-full text-left">
          <nf-error-alert [error]="error()" />
        </div>
        @if (retryable()) {
          <sk-button variant="primary" size="md" [disabled]="loading()" (skClick)="retry.emit()">
            {{ retryLabel() }}
          </sk-button>
        }
      </div>
    }
  `,
})
export class NfErrorStateComponent {
  /** The failure to show. */
  readonly error = input.required<AppError>();
  /** What failed to load, for example `No se pudieron cargar los roles`; defaults to a generic headline. */
  readonly title = input<string | undefined>(undefined);
  /** A retry is in progress: the button reads `Cargando…` and is disabled. */
  readonly loading = input<boolean>(false);
  /** Whether to offer `Reintentar`; turn it off when the host has nothing to re-run. */
  readonly retryable = input<boolean>(true);
  /** Stacked small layout for the sidebar and other narrow areas. */
  readonly compact = input<boolean>(false);

  protected readonly shownTitle = computed(() => {
    translationVersion(); // rebuilt when the language changes
    return this.title() ?? translateNow('common.errorState.title');
  });

  protected readonly retryLabel = computed(() => {
    translationVersion(); // rebuilt when the language changes
    return translateNow(this.loading() ? 'common.states.loading' : 'common.actions.retry');
  });

  /** Emitted when the user presses `Reintentar`. */
  readonly retry = output<void>();
}
